import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import {
  useCreateAIModelRevision,
  useDeleteAIModelRevision,
  useDuplicateAIModel,
  useFetchAIModelDetails,
  useGetAIModel,
  useUpdateAIModel,
} from "@via/pages/cortex/model-garden/model-garden.api.tsx";
import CreateEditProcessForm from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/create-edit-process-form.tsx";
import {
  cleanAndParseProcess,
  packProcessToOverwrite,
  packProcessToSave,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import {
  AIModelListItem,
  MenuGroupType,
  UpdateAIModelRequestBody,
} from "common";
import { useEffect, useState } from "react";
// VertexAIModelDeploymentConfig is defined inline or imported from elsewhere
type VertexAIModelDeploymentConfig = any;

interface EditAIModelFormProps {
  aiModel: AIModelListItem;
  setOpen: (open: boolean) => void;
}

const EditAIModelForm = ({ aiModel, setOpen }: EditAIModelFormProps) => {
  const [initialData, setInitialData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { mutate: duplicateAIModel, isPending: isDuplicateAIModelLoading } =
    useDuplicateAIModel();

  const {
    data: aiModelDetails,
    isLoading: isAIModelDetailsLoading,
    isError: isAIModelDetailsError,
  } = useGetAIModel(aiModel?.id, !!aiModel?.id);

  const {
    mutateAsync: updateAIModel,
    isPending: isUpdateAIModelLoading,
    isSuccess: isUpdateAIModelSuccess,
  } = useUpdateAIModel();

  const {
    mutate: createProcessRevision,
    isPending: isCreateProcessRevisionLoading,
    isSuccess: isCreateProcessRevisionSuccess,
    data: createProcessRevisionData,
  } = useCreateAIModelRevision();

  const {
    mutateAsync: deleteAIModelRevision,
    isPending: isDeleteAIModelRevisionLoading,
    isSuccess: isDeleteAIModelRevisionSuccess,
  } = useDeleteAIModelRevision();

  const { mutateAsync: fetchAIModelByProcessId } = useFetchAIModelDetails();

  useEffect(() => {
    if (createProcessRevisionData) {
      const parsed = cleanAndParseProcess(createProcessRevisionData);
      setInitialData(parsed);
      handleChangeRevision(parsed);
    }
  }, [isCreateProcessRevisionSuccess]);

  // Parse process data when model details are fetched
  useEffect(() => {
    if (aiModelDetails?.process) {
      try {
        setIsLoading(true);
        const parsed = cleanAndParseProcess(aiModelDetails.process);
        parsed.type = MenuGroupType.AI_MODEL.toString();
        parsed.modelData = aiModelDetails;
        setInitialData(parsed);
      } catch (error) {
        console.error("Failed to parse process details:", error);
      } finally {
        setIsLoading(false);
      }
    } else if (isAIModelDetailsError) {
      setIsLoading(false);
    }
  }, [aiModelDetails, isAIModelDetailsError]);

  useEffect(() => {
    if (isUpdateAIModelSuccess) {
      setOpen(false);
    }
  }, [isUpdateAIModelSuccess, setOpen]);

  useEffect(() => {
    if (isDeleteAIModelRevisionSuccess) {
      setOpen(false);
    }
  }, [isDeleteAIModelRevisionSuccess, setOpen]);

  const handleSaveProcess = async (processDraft: any) => {
    try {
      // Transform process data using packProcessToSave
      const processData = packProcessToSave(processDraft);

      await createProcessRevision({
        processId: aiModelDetails?.process?.id || aiModel.processId,
        payload: processData,
      });
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error("Failed to update AI model:", error);
    }
  };

  const handleOverwriteProcess = async (
    processDraft: any,
    _saveMode?: any,
    autoDeployConfig?: any
  ) => {
    try {
      const processData = packProcessToOverwrite(processDraft);

      // Build VertexAIModelDeploymentConfig if autoDeployEnabled or autoUndeployEnabled is set
      let deploymentConfig: VertexAIModelDeploymentConfig | null = null;

      if (
        processDraft.autoDeployEnabled !== undefined ||
        processDraft.autoUndeployEnabled !== undefined
      ) {
        const autoDeployEnabled = processDraft.autoDeployEnabled ?? false;
        const autoUndeployEnabled = processDraft.autoUndeployEnabled ?? false;

        // If both are false, set to null to clear
        if (!autoDeployEnabled && !autoUndeployEnabled) {
          deploymentConfig = null;
        } else {
          // Otherwise, create the config object
          deploymentConfig = {
            autoDeployEnabled,
            autoUndeployEnabled,
            // Only include deployment config if autoDeployEnabled is true
            ...(autoDeployEnabled &&
              autoDeployConfig && {
                autoDeployConfig: autoDeployConfig,
              }),
          };
        }
      } else if (autoDeployConfig !== undefined) {
        // If only autoDeployConfig (deployment config) is provided without enabled flags,
        // we need to preserve existing enabled states or set defaults
        // This case should not happen in normal flow, but handle it for safety
        // For now, we'll assume autoDeployEnabled should be true if config is provided
        deploymentConfig = {
          autoDeployEnabled: false,
          autoUndeployEnabled: false,
          autoDeployConfig: autoDeployConfig,
        };
      }

      // Create the AI model update payload
      const payload: UpdateAIModelRequestBody = {
        modelName:
          aiModelDetails?.config && "modelName" in aiModelDetails.config
            ? (aiModelDetails.config as any).modelName
            : undefined,
        process: processData,
        ...(deploymentConfig !== undefined && {
          deploymentConfig,
        }),
      };

      await updateAIModel({ id: aiModel.id, payload });
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error("Failed to update AI model:", error);
    }
  };

  const handleChangeRevision = (processDraft: any) => {
    const parsed = cleanAndParseProcess(processDraft);
    setInitialData(parsed);
  };

  const handleRevisionFetch = async (processId: number) => {
    const aiModelData = await fetchAIModelByProcessId(processId.toString());
    return aiModelData;
  };

  const handleDuplicateAIModel = (processId: number) => {
    duplicateAIModel(processId);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  if (isLoading || isAIModelDetailsLoading || !initialData) {
    return (
      <div className="p-4 grid grid-cols-1 gap-6 py-6 sm:grid-cols-3 lg:grid-cols-4">
        <SkeletonLoader count={12} />
      </div>
    );
  }
  if (isAIModelDetailsError) {
    return (
      <div className="via-generic-error">
        <GenericError message={ERROR} />
      </div>
    );
  }

  return (
    <CreateEditProcessForm
      title="Edit AI Model"
      data={initialData}
      isCreateProcessRevisionLoading={
        isCreateProcessRevisionLoading || isDuplicateAIModelLoading
      }
      isOverwriteRevisionLoading={isUpdateAIModelLoading}
      isCreateNewProcessLoading={false}
      isDeleteRevisionLoading={isDeleteAIModelRevisionLoading}
      onSaveProcess={handleSaveProcess}
      onChangeRevision={handleChangeRevision}
      onOverwriteProcess={handleOverwriteProcess}
      onDuplicateProcess={handleDuplicateAIModel}
      onDeleteRevision={deleteAIModelRevision}
      onCancel={handleCancel}
      onRevisionFetch={handleRevisionFetch}
    />
  );
};

export default EditAIModelForm;
